import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription, finalize, map } from 'rxjs';
import { Location } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AutoUnsub } from 'src/app/core/decorators/auto-unsub.decorator';
import { FakeFillerPipe } from 'src/app/shared/pipes/fake-filler.pipe';
import { PairingPatientModalComponent } from 'src/app/shared/components/pairing-patient-modal/pairing-patient-modal.component';
import { PairingService } from 'src/app/core/services/pairing.service';
import { Patient } from 'src/app/core/entities/patient.entity';
import { RelationType } from 'src/app/core/enums/relation-type';
import { SortableHeaderDirective } from 'src/app/shared/directives/sortable-header.directive';
import { UnpairingPatientModalComponent } from 'src/app/shared/components/unpairing-patient-modal/unpairing-patient-modal.component';
import { genderMapping } from 'src/app/core/enums/gender';

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.css'],
})
@AutoUnsub()
export class ListComponent implements OnInit {
  @ViewChildren(SortableHeaderDirective)
  headers: QueryList<SortableHeaderDirective>;
  private _observers: Subscription = new Subscription();
  private _allPatients: Patient[] = [];
  private _sourcePatients: Patient[] = [];
  private _mainResultsPage = 1;
  public searchListPatientsForm: FormGroup;
  public genderMapping = genderMapping;
  public patients: Patient[] = [];
  public loading = true;
  public page = 1;
  public itemsPerPage = 10;
  public totalPages = 1;
  public relationType = RelationType;
  public noPatientFound = false;
  public formatDate: string = 'dd/MM/yyyy';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private modalService: NgbModal,
    private fakeFillerPipe: FakeFillerPipe,
    private pairingService: PairingService,
    private location: Location
  ) {}

  ngOnInit() {
    // Prepare search patients form
    this.prepareSearchListPatientsForm();

    // Load all patients
    this.loadPatients();
    if (location.href.includes('/fr')) {
      this.formatDate = 'dd/MM/yyyy';
    }
    if (location.href.includes('/en')) {
      this.formatDate = 'MM/dd/yyyy';
    }
  }

  /**
   * Sets observers
   */
  setObservers(): void {
    this._observers.add(this.searchListPatients());
    this._observers.add(this.getQueryParams());
  }

  /**
   * Loads patients
   */
  loadPatients(): void {
    // Fill patients list with fake data
    this.patients = this.fakeFillerPipe.transform(
      this.patients,
      this.itemsPerPage
    );

    this.loading = true;
    this.pairingService
      .getMyPatients()
      .pipe(
        map((res) =>
          res.filter(
            (item) =>
              item.pairing.relationType == RelationType.ACCEPTED ||
              item.pairing.relationType == RelationType.REQUESTED
          )
        ),
        finalize(() => {
          this.setObservers();
          this.loading = false;
        })
      )
      .subscribe((res) => {
        res.sort((a, b) => {
          return (
            (a.creationDate as Date).getTime() -
            (b.creationDate as Date).getTime()
          );
        });

        this._allPatients = res;
        this._sourcePatients = this._allPatients;
        this.totalPages = Math.ceil(
          this._sourcePatients.length / this.itemsPerPage
        );
      });
  }

  /**
   * Gets query parameters
   * @returns subscription
   */
  getQueryParams(): Subscription {
    return this.route.queryParams.subscribe((params) => {
      this.page = parseInt(params['page'], 10) || this.page;

      // Save main results page
      if (this.searchListPatientsForm.get('search').value === '') {
        this._mainResultsPage = this.page;
      }

      this.patients = this._sourcePatients.slice(
        this.itemsPerPage * (this.page - 1),
        this.itemsPerPage * this.page
      );
    });
  }

  /**
   * Prepares search list form
   */
  prepareSearchListPatientsForm(): void {
    this.searchListPatientsForm = this.formBuilder.group({
      search: [''],
    });
  }

  /**
   * Searchs list patients
   * @returns subscription
   */
  searchListPatients(): Subscription {
    return this.searchListPatientsForm
      .get('search')
      .valueChanges.subscribe((res: string) => {
        // Set page
        this.page = res == '' ? this._mainResultsPage : 1;

        // Set patients source
        const regex = new RegExp(`^(${res}).*`, 'i');
        this._sourcePatients = this._allPatients.filter(
          (item) => item.lastName.match(regex) || item.firstName.match(regex)
        );

        this.totalPages = Math.ceil(
          this._sourcePatients.length / this.itemsPerPage
        );

        this.patients = this._sourcePatients.slice(
          this.itemsPerPage * (this.page - 1),
          this.itemsPerPage * this.page
        );

        if (this.patients.length === 0) {
          this.noPatientFound = true;
        }
      });
  }

  /**
   * Opens pairing modal for a patient
   * @param id patient id
   */
  openPairingPatientModal(id: string) {
    const index = this.patients.findIndex((patient) => patient.getId() == id);
    const modalRef = this.modalService.open(PairingPatientModalComponent, {});

    modalRef.componentInstance.patient = this.patients[index];
    modalRef.result.then(
      (res) => {
        if (res instanceof Patient) {
          this.router.navigate(['detail', id], { relativeTo: this.route });
        }
      },
      (err) => {
        //Do nothing
      }
    );
  }

  /**
   * Opens unpairing for a patient
   * @param id patient id
   */
  openUnpairingPatientModal(id: string): void {
    const index = this.patients.findIndex((patient) => patient.getId() == id);
    const modalRef = this.modalService.open(UnpairingPatientModalComponent, {});

    modalRef.componentInstance.patient = this.patients[index];
    modalRef.result.then(
      (res) => {
        if (res instanceof Patient) {
          this.patients.splice(index, 1);
          let i = this._allPatients.findIndex(
            (patient) => patient.getId() == id
          );
          this._allPatients.splice(i, 1);
        }
      },
      (err) => {
        //Do nothing
      }
    );
  }

  /**
   * Sorts patients list
   * @param param0 any
   */
  onSort({ column, direction }: any): void {
    const compare = (v1: string | number, v2: string | number) =>
      v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

    // resetting other headers
    this.headers.forEach((header) => {
      if (header.appSortable !== column) {
        header.direction = '';
      }
    });

    if (direction === '' || column === '') {
      direction = 'asc';
      column = 'creationDate';
    }

    let res = 0;
    this._sourcePatients.sort((a, b) => {
      switch (column) {
        case 'pairing':
          res = compare(a[column]['relationType'], b[column]['relationType']);
          break;
        default:
          const valueA =
            a[column] instanceof Date
              ? (a[column] as Date).getTime()
              : a[column];
          const valueB =
            b[column] instanceof Date
              ? (b[column] as Date).getTime()
              : b[column];
          res = compare(valueA, valueB);
          break;
      }

      return direction === 'asc' ? res : -res;
    });

    this.patients = this._sourcePatients.slice(
      this.itemsPerPage * (this.page - 1),
      this.itemsPerPage * this.page
    );
  }

  /**
   * Is patient pairing
   * @param patient Patient
   * @returns boolean
   */
  isPairing(patient: Patient): boolean {
    return (
      patient.pairing && patient.pairing.relationType == RelationType.ACCEPTED
    );
  }
}
